import React, { useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';
import useSpeechRecognition from '../../hooks/useSpeechRecognition';
import toast from 'react-hot-toast';

const Textarea = ({ 
  label, 
  id, 
  className = '', 
  error, 
  enableVoice = false,
  onChange,
  value,
  ...props 
}) => {
  const handleVoiceResult = useCallback(({ final, interim }) => {
    if (final && onChange) {
      // Append final transcript to current value
      // We need to handle the event object structure expected by onChange
      const newValue = (value ? value + ' ' : '') + final;
      const event = {
        target: {
          id,
          value: newValue,
          name: props.name
        }
      };
      onChange(event);
    }
  }, [onChange, value, id, props.name]);

  const handleVoiceError = useCallback((error) => {
    console.error('Speech recognition error:', error);
    if (error === 'not-allowed') {
      toast.error('Microphone access denied. Please check your browser settings.');
    } else if (error === 'no-speech') {
      toast('No speech detected. Please try again.', { icon: '🤔' });
    } else if (error === 'network') {
      toast.error('Network error. Speech recognition requires an internet connection.');
    } else {
      toast.error(`Speech recognition error: ${error}`);
    }
  }, []);

  const { isListening, startListening, stopListening, hasSupport } = useSpeechRecognition({
    onResult: handleVoiceResult,
    onError: handleVoiceError
  });

  const toggleListening = (e) => {
    e.preventDefault();
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return (
    <div className={className}>
      <div className="flex justify-between items-center mb-1">
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        {enableVoice && hasSupport && (
          <button
            type="button"
            onClick={toggleListening}
            className={`
              p-1.5 rounded-full transition-all duration-200 flex items-center gap-1 text-xs font-medium
              ${isListening 
                ? 'bg-red-100 text-red-600 hover:bg-red-200 animate-pulse' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-indigo-600'
              }
            `}
            title={isListening ? 'Stop recording' : 'Start voice input'}
          >
            {isListening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
            {isListening ? 'Recording...' : 'Voice Note'}
          </button>
        )}
      </div>
      <div className="relative rounded-md shadow-sm">
        <textarea
          id={id}
          value={value}
          onChange={onChange}
          className={`
            block w-full rounded-lg border-gray-200 bg-gray-50 shadow-sm 
            focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 focus:bg-white text-base py-3 px-4 transition-colors duration-200
            ${error ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50' : ''}
            ${isListening ? 'ring-2 ring-red-200 border-red-300 bg-red-50/30' : ''}
          `}
          {...props}
        />
        {isListening && (
           <div className="absolute bottom-2 right-2 flex gap-1">
             <span className="flex h-2 w-2">
               <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
               <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
             </span>
           </div>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default Textarea;
